import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useUrlTableState from "../../../hooks/useUrlTableState";
import { useAuth } from "../../../context/AuthContext";
import PermissionGate from "../../../components/common/PermissionGate";
import {
  Users,
  Search,
  ChevronDown,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  User,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  LayoutList,
  CheckSquare,
  Square,
  FileText,
  Loader2,
} from "lucide-react";
import {
  exportToCSV,
  generateExportFilename,
  downloadFromBackend,
} from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import Button from "../../../components/common/Button/Button";
import candidateService from "../../../api/services/candidateService";
import hrService from "../../../api/services/hrService";
import jobService from "../../../api/services/jobService";
import { API_BASE_URL } from "../../../api/config";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import CustomAlert from "../../../components/common/CustomAlert";
import { formatDate } from "../../../utils/stringUtils";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import CandidateBulkUploadModal from "./CandidateBulkUploadModal";
import { Upload } from "lucide-react";

const CandidateProfileManagement = ({ onViewCandidate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { alertState, showSuccess, showError, showWarning, hideAlert } = useCustomAlert();

  // URL-driven state for filters, pagination, search, and sorting
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    department: 'all',
    jobId: 'all',
    websiteRegion: 'all',
    createdBy: 'all',
    startDate: '',
    endDate: '',
    sortBy: 'appliedDate',
    sortOrder: 'desc'
  }), []);

  const {
    filters: candidateFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateCandidateFilter,
    updatePage: updateCandidatePage,
    updateLimit: updateCandidateLimit,
    updateSort: updateCandidateSort,
  } = useUrlTableState(defaultFilters);

  // Compatibility wrappers — existing JSX onChange handlers call these
  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterStatus = candidateFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updateCandidateFilter({ status });
  }, [updateCandidateFilter]);

  const filterDepartment = candidateFilters.department || 'all';
  const setFilterDepartment = useCallback((department) => {
    updateCandidateFilter({ department });
  }, [updateCandidateFilter]);

  const filterJobId = candidateFilters.jobId || 'all';
  const setFilterJobId = useCallback((jobId) => {
    updateCandidateFilter({ jobId });
  }, [updateCandidateFilter]);

  const filterWebsiteRegion = candidateFilters.websiteRegion || 'all';
  const setFilterWebsiteRegion = useCallback((websiteRegion) => {
    updateCandidateFilter({ websiteRegion });
  }, [updateCandidateFilter]);

  const filterCreatedBy = candidateFilters.createdBy || 'all';
  const setFilterCreatedBy = useCallback((createdBy) => {
    updateCandidateFilter({ createdBy, page: 1 });
  }, [updateCandidateFilter]);

  const dateRange = useMemo(() => ({
    startDate: candidateFilters.startDate || '',
    endDate: candidateFilters.endDate || ''
  }), [candidateFilters.startDate, candidateFilters.endDate]);

  const setDateRange = useCallback((range) => {
    updateCandidateFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateCandidateFilter]);

  const sortColumn = candidateFilters.sortBy || 'appliedDate';
  const sortDirection = candidateFilters.sortOrder || 'desc';

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCandidates: 0,
    hasNext: false,
    hasPrev: false,
    limit: 10,
  });
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Default creator options including Self Applied
  const defaultCreatorOptionList = useMemo(() => [
    {
      id: "self_applied",
      name: "Self Applied",
      email: "Public / Career page applicants",
      role: "Candidate",
    },
  ], []);

  // Creator filter dropdown states
  const [creatorOptions, setCreatorOptions] = useState(defaultCreatorOptionList);
  const [filteredCreatorOptions, setFilteredCreatorOptions] = useState(defaultCreatorOptionList);
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [openCreatorDropdown, setOpenCreatorDropdown] = useState(false);
  const [creatorSearchTerm, setCreatorSearchTerm] = useState('');
  const [creatorDropdownPosition, setCreatorDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const creatorDropdownRef = useRef(null);

  // View mode state
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  // Helper function to capitalize names safely
  const capitalizeName = (name) => {
    if (!name && name !== 0) return "";
    const str = typeof name === 'object' ? (name.name || name.firstName || name.title || '') : String(name);
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Helper: truncate long strings for tidy listing safely
  const truncate = (value, max = 30) => {
    if (!value && value !== 0) return "";
    const str = typeof value === 'object' ? (value.name || value.title || value.jobTitle || '') : String(value);
    return str.length > max ? str.slice(0, max - 1) + "…" : str;
  };

  const getEmailString = (email) => {
    if (!email) return "";
    if (typeof email === 'object') return email.email || email.name || "";
    return String(email).toLowerCase();
  };

  // Load departments, jobs, and creators on component mount
  useEffect(() => {
    loadDepartments();
    loadJobs();
    loadCreators();
  }, []);

  const loadCreators = async () => {
    try {
      setLoadingCreators(true);
      const res = await candidateService.getRecruitmentCreators();
      const list = res?.data || [];
      const options = [
        ...defaultCreatorOptionList,
        ...list,
      ];
      setCreatorOptions(options);
      setFilteredCreatorOptions(options);
    } catch (err) {
      console.error('Failed to load recruitment creators:', err);
      setCreatorOptions(defaultCreatorOptionList);
      setFilteredCreatorOptions(defaultCreatorOptionList);
    } finally {
      setLoadingCreators(false);
    }
  };

  // Debounced search for creator filter dropdown
  useEffect(() => {
    if (!openCreatorDropdown) return;
    const handler = setTimeout(() => {
      if (creatorSearchTerm.trim() === '') {
        setFilteredCreatorOptions(creatorOptions);
      } else {
        const searchLower = creatorSearchTerm.toLowerCase();
        const filtered = creatorOptions.filter(option =>
          (option.name && option.name.toLowerCase().includes(searchLower)) ||
          (option.email && option.email.toLowerCase().includes(searchLower)) ||
          (option.role && option.role.toLowerCase().includes(searchLower))
        );
        setFilteredCreatorOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [creatorSearchTerm, openCreatorDropdown, creatorOptions]);

  // Calculate creator dropdown position responsively
  useEffect(() => {
    const updatePosition = () => {
      if (!openCreatorDropdown || !creatorDropdownRef.current) return;
      const rect = creatorDropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Determine width clamped to available viewport space
      const targetWidth = Math.min(
        340,
        Math.max(250, Math.min(rect.width || 250, viewportWidth - 24))
      );

      // Align with button left by default, clamp so dropdown never overflows right edge
      let left = rect.left;
      if (left + targetWidth > viewportWidth - 12) {
        left = rect.right - targetWidth;
      }
      if (left + targetWidth > viewportWidth - 12) {
        left = viewportWidth - targetWidth - 12;
      }
      if (left < 12) {
        left = 12;
      }

      // Vertical positioning: default below button, flip above if constrained below
      const estimatedHeight = 310;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = rect.bottom + 6;
      if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
        top = Math.max(12, rect.top - estimatedHeight - 6);
      }

      setCreatorDropdownPosition({
        top,
        left,
        width: targetWidth
      });
    };

    if (openCreatorDropdown) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [openCreatorDropdown]);

  // Handle click outside creator dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (creatorDropdownRef.current && !creatorDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-creator-filter]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenCreatorDropdown(false);
          setCreatorSearchTerm('');
        }
      }
    };
    if (openCreatorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openCreatorDropdown]);

  // Load candidates when URL-driven filters change
  useEffect(() => {
    loadCandidates(candidateFilters.page, candidateFilters.limit);
  }, [filterStatus, filterDepartment, filterJobId, filterWebsiteRegion, filterCreatedBy, candidateFilters.search, dateRange, candidateFilters.page, candidateFilters.limit]);

  // Ref to maintain focus on search input
  const searchInputRef = useRef(null);

  // Maintain focus on search input after search
  useEffect(() => {
    if (searchInputRef.current && searchTerm) {
      searchInputRef.current.focus();
    }
  }, [candidates, searchTerm]);

  // Load departments from backend
  const loadDepartments = async () => {
    try {
      setDepartmentsLoading(true);


      const result = await hrService.getDepartments();

      if (result && result.success && result.data) {
        // Transform departments data for dropdown
        const departmentOptions = result.data.map((dept) => ({
          value: dept.name,
          label: dept.name,
        }));

        setDepartments(departmentOptions);

      } else {

        showError(result?.error || "Failed to load departments");
        // Fallback to hardcoded departments if API fails
        setDepartments([
          { value: "Engineering", label: "Engineering" },
          { value: "Sales", label: "Sales" },
          { value: "Marketing", label: "Marketing" },
          { value: "Human Resources", label: "Human Resources" },
          { value: "Operations", label: "Operations" },
          { value: "Finance", label: "Finance" },
          { value: "IT", label: "Information Technology" },
          { value: "Customer Service", label: "Customer Service" },
          { value: "Legal", label: "Legal" },
          { value: "Administration", label: "Administration" },
        ]);
      }
    } catch (error) {

      showError("Failed to load departments");
      // Fallback to hardcoded departments if API fails
      setDepartments([
        { value: "Engineering", label: "Engineering" },
        { value: "Sales", label: "Sales" },
        { value: "Marketing", label: "Marketing" },
        { value: "Human Resources", label: "Human Resources" },
        { value: "Operations", label: "Operations" },
        { value: "Finance", label: "Finance" },
        { value: "IT", label: "Information Technology" },
        { value: "Customer Service", label: "Customer Service" },
        { value: "Legal", label: "Legal" },
        { value: "Administration", label: "Administration" },
      ]);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Load active jobs from backend
  const loadJobs = async () => {
    try {
      setJobsLoading(true);


      // Fetch active jobs (or all, but user requested active)
      const result = await jobService.getJobs({ status: "active", limit: 100 });

      if (result && result.success && result.data) {
        // Extract jobs from response based on normalizeJobsResponse logic
        let jobsArray = [];
        if (Array.isArray(result.data.jobs)) {
          jobsArray = result.data.jobs;
        } else if (Array.isArray(result.data.data)) {
          jobsArray = result.data.data;
        } else if (Array.isArray(result.data)) {
          jobsArray = result.data;
        }

        const jobOptions = jobsArray.map((job) => ({
          value: job.jobId || job.id || job._id,
          label: job.title,
        }));

        setJobs(jobOptions);

      } else {

      }
    } catch (error) {

    } finally {
      setJobsLoading(false);
    }
  };

  const loadCandidates = async (page = 1, limit = pagination.limit) => {
    try {
      setLoading(true);


      const params = {
        page,
        limit,
        ...(filterStatus !== "all" && { status: filterStatus }),
        ...(filterDepartment !== "all" && { department: filterDepartment }),
        ...(filterJobId !== "all" && { jobId: filterJobId }),
        ...(filterWebsiteRegion !== "all" && { websiteRegion: filterWebsiteRegion }),
        ...(filterCreatedBy !== "all" && { createdBy: filterCreatedBy }),
        ...((candidateFilters.search || '').trim() && { search: (candidateFilters.search || '').trim() }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
      };

      const result = await candidateService.getCandidates(params);

      // Check if result has the expected structure
      if (result && result.responseCode === 200 && result.data) {
        // Standard API response format
        setCandidates(result.data.candidates || []);
        setPagination(
          result.data.pagination
            ? {
              ...result.data.pagination,
              limit: result.data.pagination.limit || limit,
            }
            : {
              currentPage: 1,
              totalPages: 1,
              totalCandidates: 0,
              hasNext: false,
              hasPrev: false,
              limit,
            },
        );

      } else if (
        result &&
        result.candidates &&
        Array.isArray(result.candidates)
      ) {
        // Direct data format (no wrapper)
        setCandidates(result.candidates);
        setPagination(
          result.pagination
            ? { ...result.pagination, limit: result.pagination.limit || limit }
            : {
              currentPage: 1,
              totalPages: 1,
              totalCandidates: result.candidates.length,
              hasNext: false,
              hasPrev: false,
              limit,
            },
        );

      } else {

        showError(result?.responseMessage || "Failed to load candidates");
        setCandidates([]);
      }
    } catch (error) {

      showError("Failed to load candidates");
      setCandidates([]);
    } finally {
      setLoading(false);

      // Maintain focus on search input after loading
      if (searchInputRef.current && searchTerm) {
        setTimeout(() => {
          searchInputRef.current.focus();
        }, 100);
      }
    }
  };

  // Permission functions (kept for backward compatibility, but use PermissionGate in JSX)
  const canViewCandidate = () => true;
  const canEditCandidate = () => true;
  const canDeleteCandidate = () => true;
  const canAddCandidate = () => true;

  const handleSearch = (e) => {
    let value = e.target.value;
    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
    value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, "");
    // Enforce max length
    if (value.length > 100) {
      value = value.substring(0, 100);
    }
    setSearchTerm(value);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };

  const handleDepartmentFilter = (department) => {
    setFilterDepartment(department);
  };

  const handleJobFilter = (jobId) => {
    setFilterJobId(jobId);
  };

  const handleViewCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    if (true) {
      if (onViewCandidate) {
        onViewCandidate(candidate);
      } else {
        navigate(`/view_candidate/${candidate.id}`, { state: { from: location.pathname + location.search } });
      }
    }
  };

  const handleEditCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    navigate(`/edit_candidate/${candidate.id}`, { state: { from: location.pathname + location.search } });
  };

  const handleDeleteCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    setCandidateToDelete(candidate);
    setShowDeleteModal(true);
  };

  const confirmDeleteCandidate = async () => {
    if (!candidateToDelete) return;

    try {

      const result = await candidateService.deleteCandidate(
        candidateToDelete.id,
      );

      if (result && result.responseCode === 200) {

        showSuccess("Candidate deleted successfully");
        loadCandidates(pagination.currentPage);
      } else {

        showError(result?.responseMessage || "Failed to delete candidate");
      }
    } catch (error) {

      showError("Failed to delete candidate");
    } finally {
      setShowDeleteModal(false);
      setCandidateToDelete(null);
    }
  };

  const cancelDeleteCandidate = () => {
    setShowDeleteModal(false);
    setCandidateToDelete(null);
  };

  // Handle column sorting
  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      updateCandidateSort(column, sortDirection === "asc" ? "desc" : "asc");
    } else {
      updateCandidateSort(column, "asc");
    }
  }, [sortColumn, sortDirection, updateCandidateSort]);

  // Get sorted candidates for the table
  const sortedCandidates = useMemo(() => {
    let rawList = (candidates || []).map(c => {
      const fName = c.firstName || (c.name ? c.name.split(' ')[0] : '') || '';
      const lName = c.lastName || (c.name ? c.name.split(' ').slice(1).join(' ') : '') || '';
      const em = c.email || '';
      const ph = c.phone || '';
      const jTitle = c.jobTitle || 'Not specified';
      const dept = c.department || c.jobDepartment || 'Not specified';
      let exp = c.experience;
      return {
        ...c,
        firstName: fName,
        lastName: lName,
        email: em,
        phone: ph,
        jobTitle: jTitle,
        department: dept,
        jobDepartment: dept,
        experience: exp
      };
    });

    if (!sortColumn || rawList.length === 0)
      return rawList;

    return [...rawList].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === "candidate") {
        aValue = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        bValue = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
      } else if (sortColumn === "job") {
        aValue = (a.jobTitle || "").toLowerCase();
        bValue = (b.jobTitle || "").toLowerCase();
      } else if (sortColumn === "experience") {
        const getExpVal = (str) => {
          if (!str) return 0;
          const match = str.toString().match(/(\d+)/);
          return match ? parseInt(match[1]) : 0;
        };
        aValue = getExpVal(a.experience);
        bValue = getExpVal(b.experience);
      } else if (sortColumn === "appliedDate") {
        aValue = new Date(a.appliedDate || 0).getTime();
        bValue = new Date(b.appliedDate || 0).getTime();
      } else if (sortColumn === "createdBy") {
        aValue = (a.createdByName || a.createdBy || "").toLowerCase();
        bValue = (b.createdByName || b.createdBy || "").toLowerCase();
      } else {
        aValue = a[sortColumn] || "";
        bValue = b[sortColumn] || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [candidates, sortColumn, sortDirection]);

  // Row selection handlers — toggle: one click = select all, next click = deselect all
  const handleSelectAll = () => {
    const allIds = candidates.map((c) => c.id || c._id);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedRows.has(id));
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(allIds));
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await candidateService.deleteCandidate(id);
        if (result && result.responseCode === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      showSuccess(
        `${successCount} candidate(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ""}`,
      );
      loadCandidates(pagination.currentPage);
    } else {
      showError("Failed to delete selected candidates");
    }
  };

  useEffect(() => {
    setSelectedRows(new Set());
  }, [filterStatus, filterDepartment, filterJobId, filterWebsiteRegion, filterCreatedBy, candidateFilters.search, dateRange, candidateFilters.page, candidateFilters.limit]);

  // Mapping function for CSV export
  const mapCandidatesForExport = (data) => {
    return data.map((candidate) => ({
      "Candidate Name":
        `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim(),
      Email: candidate.email || "",
      Phone: candidate.phone || "",
      Status: candidate.status || candidate.recruitmentStatus || "",
      "Job Title": candidate.jobTitle || "",
      Department: candidate.department || candidate.jobDepartment || "",
      "Website Region": getRegionLabel(candidate.websiteRegion) || "N/A",
      Experience: candidate.experience?.name || "N/A",
      "Applied Date": candidate.appliedDate
        ? formatDate(candidate.appliedDate, { includeTime: false })
        : "N/A",
      "Created By": candidate.createdByName || (typeof candidate.createdBy === 'string' && candidate.createdBy.length !== 24 ? candidate.createdBy : '-') || '-',
    }));
  };

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: (candidateFilters.search || '').trim() || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      department: filterDepartment !== 'all' ? filterDepartment : undefined,
      jobId: filterJobId !== 'all' ? filterJobId : undefined,
      websiteRegion: filterWebsiteRegion !== 'all' ? filterWebsiteRegion : undefined,
      createdBy: filterCreatedBy !== 'all' ? filterCreatedBy : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [candidateFilters.search, filterStatus, filterDepartment, filterJobId, filterWebsiteRegion, filterCreatedBy, dateRange.startDate, dateRange.endDate]);

  // Get current page candidate IDs
  const currentPageCandidateIds = useMemo(
    () =>
      candidates
        .map((candidate) => {
          const raw = candidate?._id ?? candidate?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [candidates]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageCandidateIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'candidate-profiles',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? candidateFilters.page,
            limit: overrideLimit ?? candidateFilters.limit,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `candidate-profiles-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      candidateFilters.page,
      candidateFilters.limit,
      sortColumn,
      sortDirection,
      currentPageCandidateIds,
    ]
  );

  // Handle export for selected candidates with format selection
  const handleCandidatesSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        showError('No selected candidates to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, showError, showSuccess]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleCandidatesScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showError('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: candidateFilters.limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageCandidateIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      candidates.length,
      baseExportFilters,
      requestBackendExport,
      candidateFilters.limit,
      currentPageCandidateIds,
      showError,
      showSuccess,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportCurrentPage = () => {
    const exportData = mapCandidatesForExport(candidates);
    exportToCSV(exportData, "candidates_page", {
      title: "Candidate Profile List",
      type: "Current Page",
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const filters = {
        startDate,
        endDate,
        status: filterStatus !== "all" ? filterStatus : undefined,
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        jobId: filterJobId !== "all" ? filterJobId : undefined,
        websiteRegion: filterWebsiteRegion !== "all" ? filterWebsiteRegion : undefined,
        search: searchTerm || undefined,
        type: `Date Range (${startDate} to ${endDate})`,
      };

      const blobData = await candidateService.exportCandidates(filters);
      if (blobData) {
        const filename = generateExportFilename("candidates_range");
        const url = window.URL.createObjectURL(blobData);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setShowExportModal(false);
        showSuccess("Candidates exported successfully!");
      }
    } catch (error) {

      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const filters = {
        status: filterStatus !== "all" ? filterStatus : undefined,
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        jobId: filterJobId !== "all" ? filterJobId : undefined,
        websiteRegion: filterWebsiteRegion !== "all" ? filterWebsiteRegion : undefined,
        search: searchTerm || undefined,
        type: "All Data",
      };

      const blobData = await candidateService.exportCandidates(filters);
      if (blobData) {
        const filename = generateExportFilename("all_candidates");
        const url = window.URL.createObjectURL(blobData);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setShowExportModal(false);
        showSuccess("All candidates export started");
      }
    } catch (error) {

      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleMoveCandidate = async (candidate, newStatus) => {
    try {
      const result = await candidateService.moveCandidate(
        candidate.id,
        newStatus,
      );
      if (result && result.responseCode === 200) {
        showSuccess(`Candidate moved to ${newStatus} successfully`);
        loadCandidates(pagination.currentPage);
      } else {
        showError(result?.responseMessage || "Failed to move candidate");
      }
    } catch (error) {

      showError("Failed to move candidate");
    }
  };

  const handleDownloadResume = async (candidate) => {
    try {
      const candidateName =
        candidate.name ||
        `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() ||
        "this candidate";
      const candidateId = candidate.id || candidate._id;


      if (!candidate.resume) {
        showWarning("Document not found");
        return;
      }

      const parseFilenameFromContentDisposition = (contentDisposition) => {
        if (!contentDisposition) return "";

        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (utf8Match?.[1]) {
          try {
            return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, "");
          } catch (_) {
            return utf8Match[1].replace(/(^"|"$)/g, "");
          }
        }

        const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
        return simpleMatch?.[1]
          ? simpleMatch[1].trim().replace(/(^"|"$)/g, "")
          : "";
      };

      const ensureFilenameHasExtension = (filename, mimeType) => {
        const clean = String(
          filename || `${candidateName.replace(/\s+/g, "_")}_Resume`,
        ).trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const type = String(mimeType || "").toLowerCase();
        if (type === "application/pdf") return `${clean}.pdf`;
        if (type === "application/msword") return `${clean}.doc`;
        if (
          type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return `${clean}.docx`;
        if (type === "text/plain") return `${clean}.txt`;
        return `${clean}.pdf`;
      };

      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/recruitment/candidates/${candidateId}/resume`,
        {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          showWarning("Document not found");
          return;
        }
        throw new Error("Failed to download resume");
      }

      const blob = await response.blob();
      const headerFileName = parseFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      );
      const fallbackFileName =
        candidate?.resume?.originalName ||
        candidate?.resume?.filename ||
        `${candidateName.replace(/\s+/g, "_")}_Resume`;
      const resolvedFileName = ensureFilenameHasExtension(
        headerFileName || fallbackFileName,
        blob.type || candidate?.resume?.mimetype || candidate?.resume?.mimeType,
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      showSuccess("Resume download started");
    } catch (error) {

      showError("Failed to download resume. Please try again.");
    }
  };

  const handleExport = async () => {
    try {
      setIsLoading(true);


      const filters = {
        status: filterStatus !== "all" ? filterStatus : undefined,
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        jobId: filterJobId !== "all" ? filterJobId : undefined,
        websiteRegion: filterWebsiteRegion !== "all" ? filterWebsiteRegion : undefined,
        search: searchTerm || undefined,
      };



      const blobData = await candidateService.exportCandidates(filters);

      if (!blobData) {
        throw new Error("No data received from export API");
      }

      if (blobData instanceof Blob && blobData.size === 0) {
        throw new Error("Empty file received from export API");
      }

      if (!(blobData instanceof Blob)) {

        throw new Error("Invalid data format received from export API");
      }

      // Create download link
      const blob = new Blob([blobData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `candidate_profiles_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);


      showSuccess("Candidates exported successfully!");
    } catch (error) {


      let errorMessage = "Error exporting candidates. Please try again.";
      if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCandidate = () => {
    // Permission check is handled by PermissionGate component
    navigate("/add_candidate", { state: { from: location.pathname + location.search } });
  };

  const handlePageChange = useCallback((page) => {
    setSelectedRows(new Set());
    updateCandidatePage(page);
  }, [updateCandidatePage]);

  const handleLimitChange = useCallback((limit) => {
    setSelectedRows(new Set());
    updateCandidateLimit(limit);
  }, [updateCandidateLimit]);

  const getStatusColor = (status) => {
    switch (status) {
      case "applied":
        return "bg-blue-100 text-blue-800";
      case "shortlisted":
        return "bg-yellow-100 text-yellow-800";
      case "interviewed":
        return "bg-purple-100 text-purple-800";
      case "interview":
        return "bg-orange-100 text-purple-800";
      case "documentation":
        return "bg-teal-100 text-teal-800";
      case "offered":
        return "bg-green-100 text-green-800";
      case "hired":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "applied":
        return <Clock className="h-4 w-4" />;
      case "shortlisted":
        return <Star className="h-4 w-4" />;
      case "interviewed":
        return <User className="h-4 w-4" />;
      case "documentation":
        return <FileText className="h-4 w-4" />;
      case "offered":
        return <CheckCircle className="h-4 w-4" />;
      case "hired":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "applied", label: "Applied" },
    { value: "shortlisted", label: "Shortlisted" },
    { value: "interviewed", label: "Interviewed" },
    { value: "documentation", label: "Documentation" },
    { value: "offered", label: "Offered" },
    { value: "hired", label: "Hired" },
    { value: "rejected", label: "Rejected" },
  ];

  const formatExperienceDisplay = (exp) => {
    if (!exp) return 'N/A';
    const val = typeof exp === 'object' ? exp?.name || '' : String(exp);
    if (!val || val.toLowerCase().includes('dummy')) return '3-5 Years';
    const lower = val.toLowerCase();
    if (
      lower.includes('year') ||
      lower.includes('level') ||
      lower.includes('fresher') ||
      lower.includes('intern') ||
      lower.includes('lead') ||
      lower.includes('senior') ||
      lower.includes('junior') ||
      lower.includes('mid') ||
      lower.includes('executive') ||
      lower.includes('entry')
    ) {
      return val;
    }
    return `${val} Years`;
  };

  // Dynamic department options with "All Departments" option
  const departmentOptions = [
    { value: "all", label: "All Departments" },
    ...departments,
  ];

  const regionOptions = [
    { value: "all", label: "All Regions" },
    { value: "MOBILOITTE", label: "Mobiloitte" },
    { value: "MOBILOITTE_UAE", label: "Mobiloitte UAE" },
    { value: "MOBILOITTE_USA", label: "Mobiloitte USA" },
    { value: "MOBILOITTE_UK", label: "Mobiloitte UK" },
    { value: "MOBILOITTE_SOUTH_AFRICA", label: "Mobiloitte South Africa" },
    { value: "MOBILOITTE_SINGAPORE", label: "Mobiloitte Singapore" },
    { value: "MOBILOITTE_GROUP", label: "Mobiloitte Group" }
  ];

  const getRegionLabel = (value) => {
    const region = regionOptions.find(r => r.value === value);
    return region && value !== "all" ? region.label : (value || "N/A");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-[10px] bg-slate-900 text-white flex items-center justify-center shadow-xs flex-shrink-0">
            <Users className="w-4 h-4 stroke-[2]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight truncate">
              Candidate Profile Management
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm mt-0.5 truncate sm:whitespace-normal">
              Manage and track candidate profiles throughout the recruitment process
            </p>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-white shadow-sm text-slate-900" : "text-gray-500 hover:text-gray-700"}`}
              title="List View"
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-all ${viewMode === "grid" ? "bg-white shadow-sm text-slate-900" : "text-gray-500 hover:text-gray-700"}`}
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected candidates`
                    : 'Export candidates'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCandidatesSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="outline"
              icon={<Upload className="h-4 w-4" />}
              onClick={() => setShowBulkUploadModal(true)}
            >
              Bulk Upload
            </Button>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="black"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleAddCandidate}
              className="!bg-slate-900 !text-white hover:!bg-slate-800"
            >
              Add Candidate
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] sm:min-w-[240px] max-w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search candidate By Name, Email or Job Title"
              value={searchTerm}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (
                  !/^[a-zA-Z0-9\s@._+-]$/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onChange={handleSearch}
              className="w-full h-[42px] pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-grow sm:flex-grow-0 min-w-[120px]">
            <select
              value={filterStatus}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors cursor-pointer"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Job Filter */}
          <div className="flex-grow sm:flex-grow-0 min-w-[130px] sm:max-w-[200px] md:max-w-xs">
            <select
              value={filterJobId}
              onChange={(e) => handleJobFilter(e.target.value)}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors cursor-pointer truncate"
              disabled={jobsLoading}
            >
              <option value="all">All Jobs</option>
              {jobsLoading ? (
                <option value="all" disabled>
                  Loading jobs...
                </option>
              ) : (
                jobs.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    title={option.label}
                  >
                    {option.label.length > 30
                      ? option.label.substring(0, 27) + "..."
                      : option.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Department Filter */}
          <div className="flex-grow sm:flex-grow-0 min-w-[130px] sm:max-w-[180px] md:max-w-xs">
            <select
              value={filterDepartment}
              onChange={(e) => handleDepartmentFilter(e.target.value)}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors cursor-pointer truncate"
              disabled={departmentsLoading}
            >
              {departmentsLoading ? (
                <option value="all">Loading departments...</option>
              ) : (
                departmentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Region Filter */}
          <div className="flex-grow sm:flex-grow-0 min-w-[120px]">
            <select
              value={filterWebsiteRegion}
              onChange={(e) => setFilterWebsiteRegion(e.target.value)}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors cursor-pointer"
            >
              {regionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Creator Filter */}
          <div className="relative flex-grow sm:flex-grow-0 min-w-[130px]">
            <button
              ref={creatorDropdownRef}
              type="button"
              onClick={() => {
                const isOpening = !openCreatorDropdown;
                setOpenCreatorDropdown(isOpening);
                if (isOpening) {
                  setFilteredCreatorOptions(creatorOptions);
                }
              }}
              disabled={loadingCreators}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <span className="truncate text-sm whitespace-nowrap min-w-[100px]">
                {filterCreatedBy === 'all'
                  ? 'All Creators'
                  : filterCreatedBy === 'self_applied'
                    ? 'Self Applied'
                    : creatorOptions.find((opt) => opt.id === filterCreatedBy)?.name || 'All Creators'}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${
                  openCreatorDropdown ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Creator Dropdown Menu */}
            {openCreatorDropdown && (
              <div
                data-dropdown-creator-filter
                className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
                style={{
                  top: `${creatorDropdownPosition.top}px`,
                  left: `${creatorDropdownPosition.left}px`,
                  width: `${creatorDropdownPosition.width}px`,
                  maxWidth: 'calc(100vw - 24px)',
                  maxHeight: 'min(360px, calc(100vh - 24px))',
                }}
              >
                <div className="p-2 border-b border-gray-100 bg-gray-50/60">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={creatorSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[^a-zA-Z0-9\s@._+-]/g, '')
                          .slice(0, 100);
                        setCreatorSearchTerm(value);
                      }}
                      placeholder="Search creators..."
                      maxLength={100}
                      className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] transition-colors"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[260px] overflow-x-hidden custom-scrollbar">
                  {loadingCreators && filteredCreatorOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      Loading creators...
                    </div>
                  ) : filteredCreatorOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      {creatorSearchTerm ? 'No creators found' : 'No creators available'}
                    </div>
                  ) : (
                    <div className="flex flex-col py-1 divide-y divide-gray-50">
                      <button
                        type="button"
                        onClick={() => {
                          setFilterCreatedBy('all');
                          setOpenCreatorDropdown(false);
                          setCreatorSearchTerm('');
                        }}
                        className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors flex flex-col ${
                          filterCreatedBy === 'all'
                            ? 'bg-blue-50/80 text-[#0F2942] font-semibold'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="font-medium">All Creators</div>
                      </button>
                      {filteredCreatorOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setFilterCreatedBy(option.id);
                            setOpenCreatorDropdown(false);
                            setCreatorSearchTerm('');
                          }}
                          className={`w-full text-left px-3.5 py-2 text-sm transition-colors flex flex-col ${
                            filterCreatedBy === option.id
                              ? 'bg-blue-50/80 text-[#0F2942] font-semibold'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div className="truncate font-medium text-gray-900 break-words" title={option.name}>
                            {option.name}
                          </div>
                          {option.email && (
                            <div className="text-xs text-gray-500 truncate break-words" title={option.email}>
                              {option.email}
                            </div>
                          )}
                          {option.role && (
                            <div className="text-xs text-gray-400 truncate capitalize break-words" title={option.role}>
                              {option.role}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Date Range Filter */}
          <div className="flex-grow sm:flex-grow-0 flex items-center">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
              containerClassName="flex items-center gap-1.5 sm:gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto"
              inputClassName="h-[42px] px-2.5 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-[#0F2942]/20 focus:border-[#0F2942] w-full sm:w-32 md:w-36 cursor-pointer bg-white transition-colors"
            />
          </div>

          {/* Clear Filters */}
          {(searchTerm ||
            filterStatus !== "all" ||
            filterDepartment !== "all" ||
            filterJobId !== "all" ||
            filterWebsiteRegion !== "all" ||
            filterCreatedBy !== "all" ||
            dateRange.startDate !== "" ||
            dateRange.endDate !== "") && (
              <div className="flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  className="h-[42px] px-3 py-2 text-sm flex items-center gap-1.5 border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    updateCandidateFilter({
                      search: "",
                      status: "all",
                      department: "all",
                      jobId: "all",
                      websiteRegion: "all",
                      createdBy: "all",
                      startDate: "",
                      endDate: "",
                      page: 1
                    });
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
        </div>
      </div>

      {/* Candidates List / Grid */}
      <div className="space-y-4">
        {candidates.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No candidates found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ||
                filterStatus !== "all" ||
                filterDepartment !== "all"
                ? "Try adjusting your search criteria"
                : "Get started by adding your first candidate"}
            </p>
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="black"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleAddCandidate}
                className="!bg-slate-900 !text-white hover:!bg-slate-800"
              >
                Add Candidate
              </Button>
            </PermissionGate>
          </div>
        ) : viewMode === "list" ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                  <tr>
                    <th className="px-6 py-2 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-0 h-4 w-4 cursor-pointer"
                        checked={
                          candidates.length > 0 &&
                          candidates.every((c) =>
                            selectedRows.has(c.id || c._id),
                          )
                        }
                        onChange={() => { }}
                        onClick={handleSelectAll}
                        title={
                          candidates.length > 0 &&
                            candidates.every((c) =>
                              selectedRows.has(c.id || c._id),
                            )
                            ? "Deselect all"
                            : "Select all"
                        }
                      />
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("candidate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Candidate</span>
                        {sortColumn === "candidate" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("job")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Job & Department</span>
                        {sortColumn === "job" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === "status" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("experience")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Experience</span>
                        {sortColumn === "experience" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("websiteRegion")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Region</span>
                        {sortColumn === "websiteRegion" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("appliedDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Applied Date</span>
                        {sortColumn === "appliedDate" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort("createdBy")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created By</span>
                        {sortColumn === "createdBy" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedCandidates.map((candidate) => (
                    <tr
                      key={candidate.id || candidate._id}
                      className={`hover:bg-blue-50/30 transition-colors ${selectedRows.has(candidate.id || candidate._id) ? "bg-blue-50/20" : ""}`}
                    >
                      <td className="px-6 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-brand focus:ring-0 h-4 w-4"
                          checked={selectedRows.has(
                            candidate.id || candidate._id,
                          )}
                          onChange={() =>
                            handleSelectRow(candidate.id || candidate._id)
                          }
                        />
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="">
                            <div className="text-sm font-medium text-gray-900">
                              {truncate(
                                `${capitalizeName(candidate.firstName)} ${capitalizeName(candidate.lastName)}`.trim() || candidate.name || candidate.candidateId || "Candidate",
                                26,
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {truncate(getEmailString(candidate.email), 28)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {truncate(typeof candidate.phone === 'object' ? candidate.phone?.number : candidate.phone, 18)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {truncate(typeof candidate.jobTitle === 'object' ? candidate.jobTitle?.title || candidate.jobTitle?.name : candidate.jobTitle, 22)}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {truncate(
                            typeof candidate.department === 'object' ? candidate.department?.name : (candidate.department || candidate.jobDepartment),
                            22,
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            typeof candidate.status === 'object' ? candidate.status?.name : candidate.status,
                          )}`}
                        >
                          {getStatusIcon(typeof candidate.status === 'object' ? candidate.status?.name : candidate.status)}
                          <span className="ml-1 capitalize">
                            {typeof candidate.status === 'object' ? candidate.status?.name || 'Active' : String(candidate.status || 'Active')}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatExperienceDisplay(candidate.experience)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {getRegionLabel(candidate.websiteRegion) || "N/A"}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(candidate.createdAt, { includeTime: true })}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium text-gray-900">
                          {candidate.createdByName || (typeof candidate.createdBy === 'string' && candidate.createdBy.length !== 24 ? candidate.createdBy : '-') || '-'}
                        </div>
                        {candidate.createdByUser?.role && (
                          <div className="text-xs text-gray-500 capitalize">
                            {candidate.createdByUser.role.replace(/_/g, ' ')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <PermissionGate
                            permission="recruitment"
                            action="read"
                          >
                            <button
                              onClick={() => handleViewCandidate(candidate)}
                              className="text-slate-900 hover:text-slate-700"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate
                            permission="recruitment"
                            action="update"
                          >
                            <button
                              onClick={() => handleEditCandidate(candidate)}
                              className="text-slate-900 hover:text-slate-700"
                              title="Edit Candidate"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          {candidate.resume && (
                            <PermissionGate
                              permission="recruitment"
                              action="read"
                            >
                              <button
                                onClick={() => handleDownloadResume(candidate)}
                                className="text-slate-900 hover:text-slate-700"
                                title="Download Resume"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          <PermissionGate
                            permission="recruitment"
                            action="delete"
                          >
                            <button
                              onClick={() => handleDeleteCandidate(candidate)}
                              className="text-slate-900 hover:text-slate-700"
                              title="Delete Candidate"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalCandidates}
              limit={pagination.limit}
              hasNextPage={pagination.hasNext}
              hasPrevPage={pagination.hasPrev}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              border={false}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedCandidates.map((candidate) => (
              <div
                key={candidate.id || candidate._id}
                className={`bg-white rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md relative group ${selectedRows.has(candidate.id || candidate._id)
                  ? "border-blue-200"
                  : "border-gray-200"
                  }`}
              >
                {/* Selection Overlay */}
                <div className="absolute top-4 left-4 z-10">
                  <button
                    onClick={() =>
                      handleSelectRow(candidate.id || candidate._id)
                    }
                    className={`transition-colors p-1 rounded-md ${selectedRows.has(candidate.id || candidate._id)
                      ? "text-brand"
                      : "text-gray-300 group-hover:text-gray-400"
                      }`}
                  >
                    {selectedRows.has(candidate.id || candidate._id) ? (
                      <CheckSquare className="h-5 w-5 fill-current" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-4 pl-8">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full bg-brand flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {capitalizeName(candidate.firstName)?.charAt(0)}
                        {capitalizeName(candidate.lastName)?.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 group-hover:text-brand transition-colors">
                          {truncate(
                            `${capitalizeName(candidate.firstName)} ${capitalizeName(candidate.lastName)}`.trim(),
                            20,
                          )}
                        </h4>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <span className="truncate max-w-[150px]">
                            {candidate.jobTitle}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(
                        candidate.status,
                      )}`}
                    >
                      {candidate.status}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <span className="truncate">{candidate.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <Star className="h-4 w-4" />
                      </div>
                      <span>
                        {formatExperienceDisplay(candidate?.experience)}
                        {!formatExperienceDisplay(candidate?.experience).toLowerCase().includes('experience') && ' Experience'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <span>
                        Region: {getRegionLabel(candidate.websiteRegion) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <Clock className="h-4 w-4" />
                      </div>
                      <span>
                        Applied{" "}
                        {candidate.appliedDate
                          ? formatDate(candidate.appliedDate, {
                            includeTime: false,
                          })
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <span className="truncate">
                        Created By:{" "}
                        <span className="font-medium text-gray-800">
                          {candidate.createdByName || (typeof candidate.createdBy === 'string' && candidate.createdBy.length !== 24 ? candidate.createdBy : '-') || '-'}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex -space-x-1">
                      <PermissionGate permission="recruitment" action="read">
                        <button
                          onClick={() => handleViewCandidate(candidate)}
                          className="p-2 text-slate-900 hover:text-slate-700 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </PermissionGate>
                      <PermissionGate permission="recruitment" action="update">
                        <button
                          onClick={() => handleEditCandidate(candidate)}
                          className="p-2 text-slate-900 hover:text-slate-700 transition-colors"
                          title="Edit Candidate"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </PermissionGate>
                      {candidate.resume && (
                        <PermissionGate permission="recruitment" action="read">
                          <button
                            onClick={() => handleDownloadResume(candidate)}
                            className="p-2 text-slate-900 hover:text-slate-700 transition-colors"
                            title="Download Resume"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      )}
                    </div>
                    <PermissionGate permission="recruitment" action="delete">
                      <button
                        onClick={() => handleDeleteCandidate(candidate)}
                        className="p-2 text-slate-900 hover:text-slate-700 transition-colors"
                        title="Delete Candidate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination for Grid View */}
      {viewMode === "grid" && (
        <PaginationControls
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalCandidates}
          limit={pagination.limit}
          hasNextPage={pagination.hasNext}
          hasPrevPage={pagination.hasPrev}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          border={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Candidate
                  </h3>
                </div>
              </div>

              <div className="mb-6 space-y-2">
                <p className="text-sm text-gray-600 break-words">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900 break-words">
                    {candidateToDelete?.firstName} {candidateToDelete?.lastName}
                  </span>
                  ?
                </p>
                <p className="text-sm text-gray-500 break-words">
                  This action cannot be undone. All candidate data, including
                  resume and application history, will be permanently deleted.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteCandidate}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <PermissionGate permission="recruitment" action="delete">
                  <button
                    onClick={confirmDeleteCandidate}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Candidate
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Selected Candidates
                  </h3>
                </div>
              </div>
              <div className="mb-6 space-y-2">
                <p className="text-sm text-gray-600">
                  You are about to permanently delete{" "}
                  <span className="font-semibold text-red-600">
                    {selectedRows.size} candidate(s)
                  </span>
                  .
                </p>
                <p className="text-sm text-gray-500">
                  This action cannot be undone. All candidate data, including
                  resumes and application history, will be permanently deleted.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-50/30 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <Loader2
                        className="animate-spin h-4 w-4 mr-2"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedRows.size} Candidate(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Candidates"
        showDateRange
        scopedFormatExport={{
          onExport: handleCandidatesScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      <CandidateBulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => loadCandidates(pagination.currentPage)}
        jobs={jobs}
        departments={departments}
      />

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default CandidateProfileManagement;
